

import { useEffect, useReducer, } from "react"
import { Box } from "@mui/material"
import DefaultModal from "../../../components/modal/DefaultModal/DefaultModal"
import lockedReducer from "../../../lib/reducer"

import api, { getEnterpriseId } from "../../../lib/api"
import { loader } from "../../../lib/loader"

import { formatPhoneNumber } from "../../../services/scout"

import Timeline from "@mui/lab/Timeline";
import TimelineItem from "@mui/lab/TimelineItem";
import TimelineSeparator from "@mui/lab/TimelineSeparator";
import TimelineConnector from "@mui/lab/TimelineConnector";
import TimelineContent from "@mui/lab/TimelineContent";
import TimelineDot from "@mui/lab/TimelineDot";
import Typography from "@mui/material/Typography";

import dayjs from "dayjs"

const qrDisplayIntervalMs = 5 * 60 * 1000
const wrongNumberDisconnectEvent = 'wrong_number_disconnect'

const statusDict = {
    connecting: {color: '#f9a825', label: 'Conectando...'},
    connect: {color: '#2e7d32', label: 'Conectado'},
    create: {color: '#7cb342', label: 'Criado'},
    disconnect: {color: '#c62828', label: 'Desconectado'},
    [wrongNumberDisconnectEvent]: {color: '#c62828', label: 'Tentativa de conexão com número incorreto'},
    remove: {color: '#6b7280', label: 'Removido'},
    qrcode: {color: '#1565c0', label: 'QR Code gerado'}
}

const eventAliases = {
    connecting: 'connecting',
    conectando: 'connecting',
    connected: 'connect',
    reconnect: 'connect',
    open: 'connect',
    opened: 'connect',
    online: 'connect',
    disconnected: 'disconnect',
    close: 'disconnect',
    closed: 'disconnect',
    offline: 'disconnect',
    created: 'create',
    removed: 'remove',
    qrcode_generated: 'qrcode',
    qr_code: 'qrcode',
    qr_code_generated: 'qrcode',
}

const normalizeEvent = (event) => {
    const normalized = String(event || '')
        .trim()
        .toLowerCase()
        .replace(/[\s.-]+/g, '_')
        .replace(/^_+|_+$/g, '')
    return eventAliases[normalized] || normalized
}

const isWrongNumberDetails = (details) => {
    const parsedDetails = parseDetails(details)
    return parsedDetails.wrongNumber === true || parsedDetails.wrongNumber === 'true'
}

const compactHistoryRows = (rows = []) => {
    const compactedRows = []
    let pendingQrRow = null
    let pendingQrTimestamp = null
    const displayedWrongNumberDisconnectDates = new Set()
    const wrongNumberDisconnectDates = new Set(
        rows
            .filter((row) => (
                normalizeEvent(row.label) === 'disconnect' &&
                isWrongNumberDetails(row.details)
            ))
            .map((row) => row.date)
    )

    const pushPendingQr = () => {
        if (pendingQrRow) {
            compactedRows.push(pendingQrRow)
            pendingQrRow = null
            pendingQrTimestamp = null
        }
    }

    rows.forEach((row) => {
        const normalized = normalizeEvent(row.label)
        const isWrongNumberDisconnect = normalized === 'disconnect' && isWrongNumberDetails(row.details)
        const rowTimestamp = Number(row.timestamp)

        if (
            normalized === 'disconnect' &&
            !isWrongNumberDisconnect &&
            wrongNumberDisconnectDates.has(row.date)
        ) {
            return
        }

        if (isWrongNumberDisconnect && displayedWrongNumberDisconnectDates.has(row.date)) {
            return
        }

        if (normalized === 'qrcode') {
            if (
                pendingQrRow &&
                Number.isFinite(rowTimestamp) &&
                Number.isFinite(pendingQrTimestamp) &&
                Math.abs(pendingQrTimestamp - rowTimestamp) < qrDisplayIntervalMs
            ) {
                pendingQrRow = row
                pendingQrTimestamp = rowTimestamp
                return
            }

            pushPendingQr()
            pendingQrRow = row
            pendingQrTimestamp = rowTimestamp
            return
        }

        pushPendingQr()

        if (isWrongNumberDisconnect) {
            displayedWrongNumberDisconnectDates.add(row.date)
        }

        compactedRows.push(row)
    })

    pushPendingQr()

    return compactedRows
}

const parseDetails = (details) => {
    if (!details) return {}
    if (typeof details === 'object') return details

    try {
        return JSON.parse(details)
    } catch {
        return {}
    }
}

const resolveStatus = (event, details) => {
    const parsedDetails = parseDetails(details)
    const normalized = normalizeEvent(event)

    if (
        normalized === 'disconnect' &&
        isWrongNumberDetails(details)
    ) {
        return statusDict[wrongNumberDisconnectEvent]
    }

    if (parsedDetails.wasReconnect === true) {
        return statusDict.connect
    }

    if (parsedDetails.autoReconnect === true) {
        return statusDict.disconnect
    }

    return statusDict[normalized] || {
        color: '#6b7280',
        label: normalized || 'Evento não identificado',
    }
}

const eventsDummy = [
  { date: "", label: "Conectando..." },
  { date: "05/03/2026 - 17:05", label: "connected" },
  { date: "04/03/2026 - 18:06", label: "connected" },
  { date: "03/03/2026 - 19:07", label: "disconnected" },
  { date: "02/03/2026 - 20:08", label: "connected" },
  { date: "01/03/2026 - 21:10", label: "connected" },
  { date: "26/02/2026 - 22:14", label: "create" },
];

const ConnectionTimeline = ({ rows }) => {

    const events = rows || eventsDummy

    return (
        <Timeline sx={{ m: 0, p: 0, maxHeight: '50vh', overflow: 'auto' }}>
            {events.map((event, i) => {
                const status = resolveStatus(event.label, event.details)

                return (
                    <TimelineItem key={`${event.date}-${event.label}-${i}`} sx={{ "&::before": { display: "none" }, minHeight: '45px' }}>
                        <TimelineSeparator>
                            <TimelineDot
                                sx={{
                                    my: '4px',
                                    bgcolor: "white",
                                    border: "2px solid",
                                    borderColor: status.color,
                                    boxShadow: "none",
                                    p: "3px",
                                }}
                            >
                                <div
                                    style={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: "50%",
                                        backgroundColor: status.color,
                                    }}
                                />
                            </TimelineDot>
                            {i < events.length - 1 && (
                                <TimelineConnector sx={{ bgcolor: "#c8e6c9" }} />
                            )}
                        </TimelineSeparator>
                        <TimelineContent sx={{ display: "flex", gap: 4, py: '2px', px: 1, minHeight: "16px", alignItems: 'top' }}>
                            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 150 }}>
                                {event.date}
                            </Typography>
                            <Typography variant="body2" sx={{ color: status.color, fontWeight: 500 }}>
                                {status.label}
                            </Typography>
                        </TimelineContent>
                    </TimelineItem>
                )
            })}
        </Timeline>
    );
}

const ConnectHistory = ({ account, handleClose, handleSuccess }) => {

    const [state, setState] = useReducer(lockedReducer, {
        rows: ''
    })

    useEffect(() => {
        const initialize = async () => {
            const enterpriseId = getEnterpriseId()
            loader.on()
            const res = await api.get(`/enterprises/${enterpriseId}/scout_accounts/${account.id}/history/`)
            if (res.status === 200) {
                setState({
                    rows: compactHistoryRows(res.data.map(row => ({
                        date: dayjs.utc(row.created_at).local().format('DD/MM/YYYY - HH:mm'),
                        timestamp: dayjs.utc(row.created_at).local().valueOf(),
                        label: row.event,
                        details: row.details,
                    })))
                })
            }
            loader.off()
        }
        initialize()
    }, [account.id])

    return (
        <DefaultModal
            title={
                'Histórico de conexões'
            }
            handleClose={handleClose}
            maxWidth="xs"
        >
            <Box display='flex' gap='1rem' flexDirection='column'>
                <Box className="bg-gray-100 rounded-xl p-4 text-center" sx={{ background: '#ebf4f6' }}>
                    <Box className="font-bold text-sm text-gray-900">{account.name}</Box>
                    <Box className="text-sm text-gray-400">{formatPhoneNumber(account.phone_number)}</Box>
                </Box>
                <ConnectionTimeline rows={state.rows} />
            </Box>
        </DefaultModal>
    )
}

export default ConnectHistory
